import React, { useMemo, useCallback, useEffect, useContext } from 'react';
import { Button, Icon, Tag, Typography } from '@scale/scaleui';
import ProcessSupervisionChunkStep from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/ProcessSupervision/ToolUseProcessSupervision/ProcessSupervisionChunkStep';
import { InteractionStepType } from '@scale/shared/types/chat';
import {
  useAllResponsesForStepType,
  useStepParams,
} from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import {
  DEFAULT_TOOL_USE_PROCESS_SUPERVISION_RATING_OPTIONS,
  ProcessSupervisionChunk,
  ToolUseProcessSupervisionActionWithUserActions,
} from '@scale/shared/types/chat/toolUseProcessSupervision';
import { invokeChatTaskTool } from 'react-scale/hooks/chatTaskTools/useInvokeChatTaskTool';
import jsonic from 'jsonic';
import {
  Editor,
  EditorContainer,
  PlainTextEditorContext,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/editor';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';

type Props = {
  action: ToolUseProcessSupervisionActionWithUserActions;
  setAction: (action: ToolUseProcessSupervisionActionWithUserActions) => void;
  deleteAction?: () => void;
  toolIds?: string[];
  responseIndex: number;
  isRunToolOnly: boolean;
};

const ToolUseProcessSupervisionActionComponent: React.FC<Props> = ({
  action,
  setAction,
  deleteAction,
  toolIds: allToolIds,
  responseIndex,
  isRunToolOnly = false,
}) => {
  const taskContext = useContext(ChatTaskContext);

  const stepParams = useStepParams<InteractionStepType.ToolUseProcessSupervision>(responseIndex);
  let toolIds = allToolIds;
  if (
    stepParams.params.tool_ids_via_template_variable &&
    stepParams.params.tool_ids_via_template_variable.length > 0
  ) {
    toolIds = [...jsonic(stepParams.params.tool_ids_via_template_variable)];
    if (!toolIds.includes('finish') && !stepParams.params.do_not_include_finish_by_default) {
      toolIds.push('finish');
    }
  }
  const {
    skip_observation: skipObservation,
    concatenateCodeFromPreviousSteps,
    run_tool_only_incorrect_rating,
  } = stepParams.params;
  const [isToolLoading, setIsToolLoading] = React.useState(false);
  const [showObservationEditor, setShowObservationEditor] = React.useState(false);

  const { hasObservation, showRunToolButton } = useMemo(() => {
    const hasObservation = action.chunks.some(chunk => chunk.name === 'Observation');
    const foundActionChunk = action.chunks.find(chunk => chunk.name === 'Action');
    const containsIncorrectRating = action.chunks.some(
      chunk => chunk.rating && chunk.rating.id === 'incorrect',
    );

    let showRunToolButton = false;
    if (
      !action.chunks.some(chunk => chunk.type === 'html') &&
      !skipObservation &&
      foundActionChunk &&
      (foundActionChunk.rewriteValue || foundActionChunk.originalValue) !== 'no_tool_selected'
    ) {
      if (action.userDeleted) {
        // If the action was deleted by the user, don't show the run tool button
        showRunToolButton = false;
      } else if (isRunToolOnly) {
        // If run tool only is enabled, always show the run tool button
        showRunToolButton = true;
      } else if (!hasObservation) {
        // If there is no observation, show the run tool button
        showRunToolButton = true;
      } else if (run_tool_only_incorrect_rating) {
        // If this specific param is enabled, only show the run tool button if the incorrect rating is present
        showRunToolButton = containsIncorrectRating;
      } else {
        showRunToolButton = true;
      }
    }

    return { hasObservation, showRunToolButton };
  }, [action, skipObservation, run_tool_only_incorrect_rating, isRunToolOnly]);

  const prompts = [
    ...useAllResponsesForStepType(InteractionStepType.PromptInput),
    ...useAllResponsesForStepType(InteractionStepType.MultiTurnContinue),
  ];
  const attachments = prompts.flatMap(prompt => prompt.context.attachments || []);
  const combinedPrompt = prompts.map(prompt => prompt.output).join('\n');

  const toolUsePSResponses = useAllResponsesForStepType(
    InteractionStepType.ToolUseProcessSupervision,
  );

  const getSetChunk =
    ({ chunkIdx }: { chunkIdx: number }) =>
    (newChunk: Partial<ProcessSupervisionChunk>) => {
      const oldChunk = action.chunks[chunkIdx];
      const newChunks = action.chunks.map((chunk, currChunkIdx) => {
        if (currChunkIdx === chunkIdx) {
          return newChunk;
        }
        return chunk;
      });

      if (newChunk.type === 'tool_select') {
        // If the tool was changed, reset the parameters for it
        const toolParamsJsonIndex = newChunks.findIndex(c => c.type === 'tool_params_json');
        if (toolParamsJsonIndex !== -1) {
          const toolParamsChunk = newChunks[toolParamsJsonIndex];
          newChunks[toolParamsJsonIndex] = {
            ...toolParamsChunk,
            rewriteValue:
              toolParamsChunk?.rating?.id === 'incorrect' ? toolParamsChunk.originalValue : null,
          };
        }
        const oldTool = oldChunk.rewriteValue || oldChunk.originalValue;
        const newTool = newChunk.rewriteValue || newChunk.originalValue;
        if (oldTool !== newTool) {
          // If tool was changed, delete observation
          const observationIndex = newChunks.findIndex(c => c.name === 'Observation');
          if (observationIndex !== -1) {
            newChunks.splice(observationIndex, 1);
          }
        }
      }

      if (newChunk.rating?.id === 'correct') {
        // clear any rewrites when we switch to 'correct'
        if (newChunk.rewriteValue) {
          newChunk.rewriteValue = null;
        }
      }

      setAction({
        // @TODO(@averyyip): Fix this typing
        // @ts-ignore
        ...action,
        chunks: newChunks as ProcessSupervisionChunk[],
      });
    };

  const getInsertChunk =
    ({ chunkIdx }: { chunkIdx: number }) =>
    (newChunk: ProcessSupervisionChunk) => {
      setAction({
        ...action,
        chunks: action.chunks.toSpliced(chunkIdx, 0, newChunk),
      });
    };

  useEffect(() => {
    if (skipObservation) return;

    const htmlChunk = action.chunks.find(chunk => chunk.type === 'html');
    if (htmlChunk) {
      const observationChunkIdx = action.chunks.findIndex(chunk => chunk.name === 'Observation');
      const observationChunk = action.chunks[observationChunkIdx];
      const hasObservationChunk = observationChunkIdx !== -1;
      const htmlChunkValue = htmlChunk.rewriteValue ?? htmlChunk.originalValue;
      const newChunk: ProcessSupervisionChunk = {
        name: 'Observation',
        type: 'html_rendered',
        originalValue: htmlChunkValue,
        rewriteValue: null,
        readOnly: true,
        rating: null,
        parent: {
          action: 'html',
          actionInput: htmlChunkValue,
        },
      };
      if (!hasObservationChunk) {
        const insertChunk = getInsertChunk({ chunkIdx: action.chunks.length });
        insertChunk(newChunk);
      } else if (observationChunk.originalValue !== htmlChunkValue) {
        const setChunk = getSetChunk({ chunkIdx: observationChunkIdx });
        setChunk(newChunk);
      }
    }
  }, [action.chunks, skipObservation]);

  const toolId = useMemo(() => {
    const { chunks } = action;
    const actionChunk = chunks.find(chunk => chunk.name === 'Action');
    return actionChunk?.rewriteValue || actionChunk?.originalValue;
  }, [action]);

  const generateObservation = useCallback(async () => {
    if (skipObservation) return;
    setShowObservationEditor(false);

    const { chunks } = action;
    const actionChunk = chunks.find(chunk => chunk.name === 'Action');
    const actionInputChunk = chunks.find(chunk => chunk.name === 'Action Input');
    if (!actionChunk || !actionInputChunk) {
      throw new Error('ToolUseProcessSupervision: Action and Action Input must be defined');
    }

    const params =
      actionInputChunk.rating?.id === 'incorrect' && actionInputChunk.rewriteValue
        ? actionInputChunk.rewriteValue
        : actionInputChunk.originalValue;

    const toolId =
      actionChunk.rating?.id === 'incorrect' && actionChunk.rewriteValue
        ? actionChunk.rewriteValue
        : actionChunk.originalValue;

    setIsToolLoading(true);

    if (!toolId) {
      throw new Error('ToolUseProcessSupervision: Tool ID must be defined to generate observation');
    }

    const getConcatenatedCodeFromPreviousSteps = () => {
      const prevCodeChunks = toolUsePSResponses.map(resp => {
        const chunks = resp.context.actions[0]?.chunks ?? [];
        const isPythonInterpreter = !!chunks.find(
          chunk => (chunk.rewriteValue || chunk.originalValue) === 'python_interpreter',
        );
        const toolParamsChunk = chunks.find(chunk => chunk.type === 'tool_params_json');
        if (!toolParamsChunk || !isPythonInterpreter) {
          return '';
        }
        return JSON.parse(toolParamsChunk?.rewriteValue || toolParamsChunk?.originalValue).code;
      });
      const prevCodeWithPrints = prevCodeChunks.join('\n');
      const prevLines = prevCodeWithPrints.split('\n');
      const prevCode = prevLines.map(line => {
        if (line.includes('print(')) {
          return line.replace('print(', '').replace(new RegExp(/\)$/), '');
        }
        return line;
      });
      const code = [...prevCode, JSON.parse(params).code].join('\n');
      return code;
    };

    const getParams = () => {
      if (toolId === 'python_interpreter' && concatenateCodeFromPreviousSteps) {
        const code = getConcatenatedCodeFromPreviousSteps();
        return {
          code,
          attachments,
        };
      }
      return {
        ...JSON.parse(params),
        attachments,
      };
    };

    let newChunk: ProcessSupervisionChunk;

    try {
      const resp = await invokeChatTaskTool({
        toolId,
        params: getParams(),
        taskMetadata: {
          modelName: stepParams.params.modelName,
          userPrompt: combinedPrompt,
          assignmentId: taskContext.assignmentId,
          projectId: taskContext.projectId,
        },
      });

      const getBase64Image = () => {
        if (toolId !== 'python_interpreter') {
          return null;
        }
        const base64ImageMatches = (resp?.stdout as string | undefined)?.match(
          /```base64_image\s+([\s\S]*?)\s+```/,
        );
        return base64ImageMatches ? base64ImageMatches[1] : null;
      };

      const base64Image = getBase64Image();
      newChunk = {
        name: 'Observation',
        type: base64Image ? 'base64_image' : 'json',
        originalValue: base64Image ?? JSON.stringify(resp || {}),
        rewriteValue: null,
        readOnly: true,
        rating: null,
        parent: {
          action: toolId,
          actionInput: params,
        },
      };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      newChunk = {
        name: 'Observation',
        type: 'json',
        originalValue: `Error executing tool: ${e.message}`,
        rewriteValue: null,
        readOnly: true,
        rating: null,
        parent: {
          action: toolId,
          actionInput: params,
        },
      };
    }

    const observationIndex = chunks.findIndex(chunk => chunk.name === 'Observation');
    if (observationIndex !== -1) {
      const setChunk = getSetChunk({ chunkIdx: observationIndex });
      setChunk(newChunk);
    } else {
      const insertChunk = getInsertChunk({ chunkIdx: chunks.length });
      insertChunk(newChunk);
    }
    setIsToolLoading(false);
  }, [action, getInsertChunk, getSetChunk, setIsToolLoading, skipObservation, attachments]);

  // Get Observation chunk value
  const observationIndex = action.chunks.findIndex(chunk => chunk.name === 'Observation');
  const observationChunk = observationIndex !== -1 ? action.chunks[observationIndex] : null;
  return (
    <div
      className={`w-full border border-neutral-300 rounded-md ${
        action.userDeleted ? 'opacity-50' : ''
      }`}
    >
      <div className="flex justify-between items-center px-4 py-2 rounded-t-md border-b border-neutral-300 bg-neutral-1">
        <Typography.Subtitle2>Thought and/or Action</Typography.Subtitle2>
        {deleteAction && action.userDeleted !== true ? (
          <Button variant="secondary" onClick={deleteAction}>
            <Icon icon="trash" variant="neutral" size="sm" />
          </Button>
        ) : (
          <Button variant="secondary" onClick={() => setAction({ ...action, userDeleted: false })}>
            Restore Action
          </Button>
        )}
      </div>
      <div className="flex flex-col gap-3 py-3 px-4">
        {action.chunks
          .map((chunk, originalChunkIdx) => {
            if (chunk.hidden) return null;
            if (skipObservation && chunk.name === 'Observation') return null;
            return (
              <div key={originalChunkIdx}>
                <Typography.Subtitle3 className="flex flex-col items-start gap-2">
                  <Tag
                    className="mb-2"
                    size="lg"
                    variant={action.userDeleted ? 'neutral' : 'primary'}
                  >
                    {chunk.displayName || chunk.name}
                  </Tag>
                </Typography.Subtitle3>
                <ProcessSupervisionChunkStep
                  chunk={chunk}
                  ratingOptions={DEFAULT_TOOL_USE_PROCESS_SUPERVISION_RATING_OPTIONS}
                  setChunk={getSetChunk({ chunkIdx: originalChunkIdx })}
                  disabled={chunk.readOnly || isRunToolOnly || action.userDeleted}
                  toolIds={toolIds}
                  selectedToolId={toolId}
                />
              </div>
            );
          })
          .filter(Boolean)}
        {(showObservationEditor || observationChunk?.rewriteValue) && (
          <div>
            <Typography.Subtitle3>Overwritten Observation</Typography.Subtitle3>
            <PlainTextEditorContext
              initialContent={observationChunk?.rewriteValue ?? observationChunk?.originalValue}
            >
              <EditorContainer className="w-full mb-4">
                <Editor
                  value={observationChunk?.rewriteValue ?? observationChunk?.originalValue}
                  onContentChange={content => {
                    const setChunk = getSetChunk({ chunkIdx: observationIndex });
                    setChunk({ ...observationChunk, rewriteValue: content });
                  }}
                />
              </EditorContainer>
            </PlainTextEditorContext>
          </div>
        )}
      </div>
      <div className="flex justify-end pb-2 pr-2">
        {stepParams.params.can_edit_observation &&
          hasObservation &&
          !showObservationEditor &&
          !observationChunk?.rewriteValue && (
            <Button
              onClick={() => setShowObservationEditor(true)}
              isLoading={isToolLoading}
              disabled={isToolLoading}
            >
              Edit Observation
            </Button>
          )}
        {showRunToolButton && (
          <Button
            className="ml-4"
            onClick={() => generateObservation()}
            isLoading={isToolLoading}
            disabled={isToolLoading}
            data-testid="run-tool-button"
          >
            {hasObservation ? 'Rerun Tool' : 'Run Tool'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default ToolUseProcessSupervisionActionComponent;
